import './pm/view-once.js';
/**
 * PM Orchestrator — initializes the private messages interface.
 *
 * All logic has been extracted into modules under pm/*.js.
 * This file handles document.ready initialization and event wiring only.
 *
 * Module load order (via webpack entry):
 *   pm/state.js            — global state variables
 *   pm/drafts.js           — localStorage draft persistence
 *   pm/typing.js           — WebSocket typing/newmessage handlers
 *   pm/scroll.js           — scroll management
 *   pm/layout.js           — responsive layout, draggable separator, placeholder panel
 *   pm/polling.js          — periodic AJAX update polling, read receipts
 *   pm/crud.js             — contact management, message/thread operations, search
 *   pm/thread-view.js      — open/close threads, header updates
 *   pm/message-actions.js  — message action menus, reply, swipe-to-reply
 *   pm/pagination.js       — paginated view mode
 *   pm/calendar-jump.js    — date-based message navigation
 *   pm/legacy.js           — show_msg_history (earlier-messages loader)
 */

import { prefersPagedView } from './pm/view-preference.js';
import { createMobileKeyboardViewport } from './pm/mobile-keyboard-viewport.js';
import { attachmentOptions } from './pm/attachment-options.js';

// Onload
$(document).ready(function() {
  if ($(".privateMessages").length === 0) {
    return;
  }
  window.pm_ajax_request_active = 0;
  window.messages_highlighted = 1;

  // Initialize scroll for direct navigation (thread open on page load)
  var threadsRight = $("#threads_right");
  var threadsMain = $("#threads_main");
  if (threadsRight.length > 0 && threadsMain.hasClass("threads--open")) {
    window.setupScrollObserver(threadsRight);
    // If user prefers paginated view, switch to it instead of scrolling to bottom
    if (prefersPagedView()) {
      setTimeout(function() { window.activatePagedView(); }, 200);
    } else {
      setTimeout(function() {
        window.scrollToBottomAfterImagesLoad(threadsRight, 3000);
      }, 100);
    }
    // Consolidate read receipts after initial render
    setTimeout(function() {
      if (window.consolidateReadReceipts) window.consolidateReadReceipts();
    }, 200);
  }

  // Handle orientation changes on mobile
  var resizeTimeout;
  $(window).on('resize orientationchange', function() {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(function() {
      var tr = $("#threads_right");
      if (tr.length > 0 && tr.find('.msg').length > 0 && window.isNearNewestMessage(tr[0])) {
        window.scrollToBottomEnhanced(tr, 100, false);
      }
    }, 250);
  });

  // Stop tab title flashing when user focuses the window
  $(window).on('focus', function() {
    if (typeof window.stopFlashTitle === 'function') {
      window.stopFlashTitle();
    }
  });
  window.last_ads_reload = new Date().getTime();
  // Avoid multiple calls of load more threads called by scroll
  window.load_more_threads_active = 0;
  // Avoid multiple calls of load_more_msg
  window.load_more_msg_active = 0;
  // Array of drafts
  window.last_draft = {};
  // Initialize dragging state
  window.isDragging = false;

  // Only autofocus on desktop to avoid opening virtual keyboard on mobile
  if (window.matchMedia('(min-width: 640px)').matches) {
    $("#message").focus();
  }

  // Restore local draft, overriding stale server-rendered draft
  var initialThreadId = $("#send_thread_id").val();
  if (initialThreadId) {
    var localDraft = window.draftStorage.load(initialThreadId);
    var serverDraft = $("#message").val()?.trim() || '';
    if (localDraft !== null && localDraft !== serverDraft) {
      $("#message").val(localDraft);
      if (localDraft) {
        $("#threads_main").addClass("send");
      } else {
        $("#threads_main").removeClass("send");
      }
    }
    // The draft write above fires no input event.
    window.PmReplyOpeners?.sync();
  }

  // Detect view mode
  window.pm_view_mode = $("#threads_main").hasClass("paged") || $("#single_thread_wrapper").length ? "paged" : "chat";

  if (window.pm_view_mode === "paged") {
    // Load thread on click
    $(document).on("click", ".pm_thread", function(e) {
      // Do not run when clicked on buttons
      if ($(e.target).hasClass("fa")) return false;
      if ($(e.target).hasClass("thumb")) return true;

      // Get thread ID
      var id = $(this).attr("id").replace("thread_", "");

      window.location.href = window.location.protocol + "//" +
        window.location.hostname + window.location.pathname +
        "?thread_id=" + id;
      return false;
    });

    // Remove highlighting from new messages in 3s
    window.setTimeout(function() {
      window.remove_new_mark();
    }, 3000);

    // If user push back btn in classic view after using split view
    if (window.htm5_history_supported) {
      // Listen to htm5 history events
      window.addEventListener("popstate", function(e) {
        var requestsView = window.PmRequests?.syncWithHistory?.(e.state);
        if (requestsView && requestsView !== 'keep') return false;
        if (window.pm_without_requests_view(e.state)) {
          // Redirect to url in path (simulate browser history)
          let loc = window.location.protocol + "//" +
            window.location.hostname + window.location.pathname;
          if (typeof e.state.thread_id != "undefined" && !isNaN(e.state.thread_id) &&
            (e.state.thread_id != "")) {
            loc += "?thread_id=" + e.state.thread_id;
          }
          window.location.href = loc;
        }
        return false;
      }, false);
    }
  }

  // Enter runs the search and Escape closes it, except while an IME composes text.
  // Keydown, because the keyup that ends a composition no longer reports it.
  $(document).on("keydown", "#username_filter", function(e) {
    if (e.originalEvent.isComposing || e.keyCode === 229) {
      return;
    }
    if (e.keyCode === 13) {
      window.find_contact();
    } else if (e.keyCode === 27) {
      closePmSearch();
    }
  });
  $(document).on("compositionend", "#username_filter", function() {
    window.debounce_find_contact(this);
  });
  $(document).on("keydown", "#pm-search-close", function(e) {
    if (e.keyCode === 27) {
      closePmSearch();
    }
  });

  // The search field takes the place of the filter tabs while it is open
  function openPmSearch() {
    $("#pm-filter-group").removeClass("contents").addClass("hidden");
    $("#pm-search-toggle").removeClass("flex").addClass("hidden");
    $("#pm-search, #pm-search-close").removeClass("hidden").addClass("flex");
    $("#username_filter").trigger("focus");
  }

  function closePmSearch() {
    const input = $("#username_filter");
    // A search the server applied is in the URL, so closing it reloads the full list.
    if ($("#filters").attr("data-filter") === "username") {
      input.val("");
      window.find_contact();
      return;
    }
    input.val("");
    $("#pm-search, #pm-search-close").removeClass("flex").addClass("hidden");
    $("#pm-search-toggle").removeClass("hidden").addClass("flex");
    $("#pm-filter-group").removeClass("hidden").addClass("contents");
    $("#pm-search-toggle").trigger("focus");
  }

  // A search loads a new page, so focus returns to the field with the caret at the end.
  const searchInput = document.getElementById("username_filter");
  // A touch search ends on Enter, so the keyboard stays closed over the results.
  if (searchInput && $("#filters").attr("data-filter") === "username" && window.matchMedia("(pointer: fine)").matches) {
    searchInput.focus();
    searchInput.setSelectionRange(searchInput.value.length, searchInput.value.length);
  }

  $(document).on("click", "#pm-search-toggle", function(e) {
    e.preventDefault();
    openPmSearch();
  });

  $(document).on("click", "#pm-search-close", function(e) {
    e.preventDefault();
    closePmSearch();
  });

  // Run add contact on enter
  $(document).on("keyup", "#new_contact", function(e) {
    if (e.keyCode === 13) {
      window.add_contact();
    }
  });

  // Regular updates
  window.run_update_process();

  // Chat view mode related functions
  if (window.pm_view_mode === "chat") {
    // Load more messages on scroll up or "Load more" link click
    // With column-reverse, scrollTop=0 is bottom; scrolling UP makes scrollTop negative
    $("#threads_right").scroll(function() {
      // Show/hide jump-to-latest button based on scroll position
      var jumpBtn = document.getElementById('jump-to-latest-btn');
      var inDateJump = window.calendarJump && window.calendarJump.dateJumpActive;
      if (jumpBtn && !window.pagedViewActive) {
        if (inDateJump || Math.abs(this.scrollTop) > 300) {
          // The decision bar holds the compose slot for a pending Request, and
          // a hidden compose box measures 0.
          var bottomUi = window.pm_bottom_ui ? window.pm_bottom_ui() : null;
          jumpBtn.style.bottom = (bottomUi ? bottomUi.offsetHeight + 12 : 80) + 'px';
          jumpBtn.classList.remove('hidden');
          jumpBtn.classList.add('flex');
        } else {
          jumpBtn.classList.add('hidden');
          jumpBtn.classList.remove('flex');
        }
      }

      // In date-jump mode: load older messages when scrolling to top
      if (inDateJump) {
        if (this.scrollTop <= 5 && !window.pm_ajax_request_active) {
          window.calendarJump._loadOlderMessages();
        }
        return;
      }

      // Avoid multiple calls; skip in paginated view mode
      if (window.load_more_msg_active == 1 || window.pagedViewActive) return;
      var el = this;
      if (Math.abs(el.scrollTop) >= el.scrollHeight - el.clientHeight - 5) window.load_more_msg();
    });
    $(document).on("click", "#load_older_messages", window.load_more_msg);

    // Remove "new" in 3 seconds
    window.setTimeout(function() {
      window.remove_new_mark();
    }, 3000);

    // Load thread on click
    $(document).on("click", ".pm_thread", function(e) {
      var target = $(e.target);
      var avatar = target.closest(".thread-avatar");
      var action = window.pm_thread_row_click_action({
        isIcon: target.hasClass("fa"),
        inButton: target.is("button") || target.closest("button").length > 0,
        inRowActions: target.closest(".thread-swipe-actions").length > 0
          || target.closest(".thread-desktop-actions").length > 0,
        isDragging: !!window.isDragging,
        avatarHref: avatar.length ? avatar.attr("href") : null
      });

      if (action === "ignore") return false;
      // The photo is an anchor to the profile, and the browser follows it.
      if (action === "link") return true;

      e.preventDefault();

      // Get thread ID
      let id = $(this).attr("id").replace("thread_", "");

      // If html5 history not supported use redirect
      if (!window.htm5_history_supported) {
        window.location.href = window.location.protocol + "//" +
          window.location.hostname + window.location.pathname +
          "?thread_id=" + id;
        return false;
      }

      // Push url to html5 history
      let stateObj = { "thread_id": id };
      history.pushState(stateObj, null, "/app/messages?thread_id=" + id);

      window.view_thread(id, true);
    });

    if (window.htm5_history_supported) {
      // Listen to htm5 history events
      window.addEventListener("popstate", function(e) {
        // The requests view first: its entry shares the URL of the inbox entry under it.
        var requestsView = window.PmRequests?.syncWithHistory?.(e.state);
        var urlThreadId = window.get_active_thread_id();
        var urlThreadRow = urlThreadId ? document.getElementById("thread_" + urlThreadId) : null;
        var entry = window.pm_popstate_step({
          state: e.state,
          requestsView: requestsView,
          url: window.location.pathname + window.location.search + window.location.hash,
          urlThreadId: urlThreadId,
          // A hidden row is the stub that requests.js adds for a message
          // request. The server renders that row unhidden inside a hidden
          // #pm-active-request-row, so the host has to be tested too.
          urlThreadListed: !!urlThreadRow && !urlThreadRow.classList.contains("hidden")
            && !urlThreadRow.closest("#pm-active-request-row"),
          openThreadId: $("#threads_main").hasClass("threads--open") ? $("#send_thread_id").val() : null
        });
        if (entry.replace) history.replaceState(entry.replace.state, null, entry.replace.url);
        if (entry.action === 'open') {
          window.view_thread(entry.threadId, true);
          // A peek pushes a state-carrying entry, which reaches here without
          // passing the urlThreadListed guard. The bar has to name this sender.
          window.PmRequests?.restoreRequestBar?.(entry.threadId);
        } else if (entry.action === 'close') {
          window.close_thread(true);
        }

        return false;
      }, false);
    }

    // Persist scroll position on thread list scroll
    $("#threads_left").scroll(function() {
      try { sessionStorage.setItem('pm_threads_scroll', this.scrollTop); } catch(e) {}
    });

    // Auto-load older conversations when scrolled near the bottom of the thread list.
    window._threadsFullyLoaded = !document.getElementById('load_more_conversations');
    var threadsLeftEl = document.getElementById('threads_left');
    if (threadsLeftEl) {
      threadsLeftEl.addEventListener('scroll', function() {
        if (window._threadsFullyLoaded || window.load_more_threads_active) return;
        var el = this;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 300) {
          window.load_older_conversations(false);
        }
      });
    }

    // Resize to fit screen
    // window.threads_main_resize();

    if (!window.is_mobile_browser()) {
      // Detect resize end and run resize func
      let rtime = new Date(1, 1, 2000, 12, 0, 0);
      let timeout = false;
      let delta = 500;
      $(window).resize(function() {
        rtime = new Date();
        if (timeout === false) {
          timeout = true;
          setTimeout(window.resizeend, delta);
        }
      });

      window.resizeend = function() {
        if (new Date() - rtime < delta) {
          setTimeout(window.resizeend, delta);
        } else {
          timeout = false;
          // window.threads_main_resize();
        }
      };
    }

    // If opening active thread by direct link. A Message Request has no
    // Primary row, so its row is carried outside #threads_left — see the
    // #pm-active-request-row comment in pm/index.twig.
    let activeThread = $("#threads_left .pm_thread.active");
    let carriedRow = false;
    if (!activeThread.length) {
      activeThread = $("#pm-active-request-row .pm_thread.active");
      carriedRow = activeThread.length > 0;
    }
    if (activeThread.length) {
      // Restore saved scroll position, or scroll active thread into view
      var savedScroll = null;
      try { savedScroll = sessionStorage.getItem('pm_threads_scroll'); } catch(e) {}
      var restoreTarget = savedScroll !== null ? parseInt(savedScroll, 10) : null;
      var tlEl = document.getElementById('threads_left');
      if (restoreTarget !== null) {
        tlEl.scrollTop = restoreTarget;
        setTimeout(function() { tlEl.scrollTop = restoreTarget; }, 0);
        setTimeout(function() { tlEl.scrollTop = restoreTarget; }, 50);
        setTimeout(function() { tlEl.scrollTop = restoreTarget; }, 200);
      } else if (!carriedRow) {
        // A hidden row has no box to scroll to: its rect is all zeros.
        window.scroll_thread_to_middle(activeThread);
      }
      window.open_active_thread_by_link(activeThread);
    }

  } // Chat view related functions END

  // Resize message input textarea to fit content
  const MAX_TEXTAREA_HEIGHT = 128;

  function autoResizeTextarea() {
    const el = this;

    // Reset height to 0 so scrollHeight reflects actual content size
    el.style.height = '0';
    var baseHeight = el.scrollHeight;

    if (baseHeight > MAX_TEXTAREA_HEIGHT) {
      el.style.height = MAX_TEXTAREA_HEIGHT + 'px';
      el.style.overflowY = 'auto';
    } else {
      el.style.height = baseHeight + 'px';
      el.style.overflowY = 'hidden';
    }

    // Keep messages scrolled to bottom when textarea resizes
    // With column-reverse, scrollTop=0 is bottom
    var threadsRight = document.getElementById('threads_right');
    if (threadsRight && window.isNearNewestMessage(threadsRight)) {
      threadsRight.scrollTop = 0;
    }
  }

  $("#message").on("input", autoResizeTextarea);

  // Resize textarea on load if it already has content (e.g. server-rendered draft)
  var msgEl = document.getElementById('message');
  if (msgEl && msgEl.value.length > 0) {
    autoResizeTextarea.call(msgEl);
  }

  // Fade "Enter to send" hint based on input content (visibility gated by CSS: hidden sm:block)
  var sendHint = document.getElementById('send_hint');
  if (sendHint) {
    sendHint.style.opacity = '1';
    $("#message").on("input", function() {
      sendHint.style.opacity = this.value.trim() ? '0' : '1';
    });
  }

  // Send message when pressing enter on desktop; on mobile, Enter inserts newline
  $("#message").on("keydown", function(e) {
    if (e.key === "Enter" && !e.shiftKey && window.matchMedia('(min-width: 640px)').matches) {
      e.preventDefault();
      $("#msg_submit").click();
    }
  });

  // Soft-keyboard handling for the fixed phone layout, which can start or end after load
  var pmContainer = document.querySelector('.privateMessages');
  var keyboardTimer = null;

  function isTextEntry(el) {
    if (!el) return false;
    if (el.tagName === 'TEXTAREA') return true;
    return el.tagName === 'INPUT'
      && !/^(button|checkbox|color|date|datetime-local|file|hidden|image|month|radio|range|reset|submit|time|week)$/i.test(el.type);
  }

  function scrollMessagesToBottom(force) {
    // With column-reverse, scrollTop=0 is bottom
    var threadsRight = document.getElementById('threads_right');
    if (threadsRight && (force || window.isNearNewestMessage(threadsRight))) {
      threadsRight.scrollTop = 0;
    }
  }

  if (pmContainer) {
    var keyboardViewport = createMobileKeyboardViewport(pmContainer, scrollMessagesToBottom);

    document.addEventListener('focusin', function(e) {
      if (isTextEntry(e.target)) {
        clearTimeout(keyboardTimer);
        keyboardViewport.open();
      }
    });

    document.addEventListener('focusout', function(e) {
      if (isTextEntry(e.target)) {
        keyboardTimer = setTimeout(function() {
          if (!keyboardViewport.close()) return;
          // Re-pin after the iOS keyboard-dismiss animation, which fires no window resize.
          setTimeout(function() { window.threads_main_resize(); }, 400);
        }, 100);
      }
    });
  }

  // Update send button state based on message content and photo
  function updateSendButtonState() {
    window.viewOnceComposer?.refresh();
    const $submitBtn = $("#msg_submit");
    const messageText = $("#message").val()?.trim() || '';
    const hasPhotoSelected = window.photoUpload && window.photoUpload.hasPhoto();
    const hasAudioReady = window.audioRecord && window.audioRecord.hasAudio();
    const hasVideoSelected = Boolean(window.videoSend?.hasVideo());
    const hasContent = messageText.length > 0 || hasPhotoSelected || hasAudioReady || hasVideoSelected;

    // A clip that a send holds goes out with that send.
    $submitBtn.prop("disabled", !hasContent || Boolean(window.videoSend?.isSending()));

    // Also toggle classes for visual state
    if (hasContent) {
      $submitBtn.removeClass("bg-gray-200 text-gray-400").addClass("bg-sky-500 text-white");
    } else {
      $submitBtn.removeClass("bg-sky-500 text-white").addClass("bg-gray-200 text-gray-400");
    }

    const options = attachmentOptions({
      hasText: messageText.length > 0,
      hasPhoto: Boolean(hasPhotoSelected),
      hasVideo: hasVideoSelected,
    });
    $("#mic_btn").toggleClass('hidden', !options.mic);
    $("#camera_btn").toggle(options.camera);
  }

  // Update button state on input changes and persist draft locally
  $("#message").on("input", function () {
    updateSendButtonState();
    const threadId = $("#send_thread_id").val();
    window.draftStorage.save(threadId, $(this).val().trim());
  });

  // Expose function globally for photo upload module to call after selecting/removing photos
  window.updateSendButtonState = updateSendButtonState;

  // Initial state check
  updateSendButtonState();

  // Device detection, the swipe/desktop interface toggle and the resize handler
  // live in the shared swipe-actions module. This alias lets the thread list's
  // several re-render paths (polling, crud, message-sender) reapply the correct
  // interface to freshly rendered rows.
  window.initializeThreadActions = function() {
    window.SwipeActions && window.SwipeActions.refresh();
  };

  // Toggle the compact filter dropdown
  $("#filters-compact-toggle").on("click", function(e) {
    e.preventDefault();
    e.stopPropagation();
    $("#pm-options-menu").addClass("hidden");
    $("#conversation-actions-menu").addClass("hidden");
    var menu = $("#filters-compact-menu");
    menu.toggleClass("hidden");
    if (!menu.hasClass("hidden")) {
      window.LucideIcons && window.LucideIcons.initialize(menu[0]);
    }
  });

  // Toggle the options menu
  $("#pm-options-toggle").on("click", function(e) {
    e.preventDefault();
    e.stopPropagation();
    // Close other menus first
    $("#filters-compact-menu").addClass("hidden");
    $("#conversation-actions-menu").addClass("hidden");
    $("#pm-options-menu").toggleClass("hidden");
  });

  // Toggle the conversation actions menu
  $("#conversation-actions-toggle").on("click", function(e) {
    e.preventDefault();
    e.stopPropagation();
    // Close other menus first
    $("#filters-compact-menu").addClass("hidden");
    $("#pm-options-menu").addClass("hidden");
    $("#conversation-actions-menu").toggleClass("hidden");
  });

  // Close menus when clicking outside
  $(document).on("click", function(e) {
    if (!$(e.target).closest("#filters-compact-toggle, #filters-compact-menu").length) {
      $("#filters-compact-menu").addClass("hidden");
    }
    if (!$(e.target).closest("#pm-options-toggle, #pm-options-menu").length) {
      $("#pm-options-menu").addClass("hidden");
    }
    if (!$(e.target).closest("#conversation-actions-toggle, #conversation-actions-menu").length) {
      $("#conversation-actions-menu").addClass("hidden");
    }
  });

  // Prevent clicks inside dropdown menus from propagating to thread handlers
  $("#pm-options-menu, #conversation-actions-menu").on('click', function(e) {
    e.stopPropagation();
    if ($(e.target).is('a') || $(e.target).closest('a').length) {
      $(this).addClass("hidden");
    }
  });

  // Handle thread-level action buttons (desktop only)
  $(document).on("click", ".thread-action-btn", function(e) {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    const $button = $(this);
    // Read as a raw attribute. jQuery's .data() coerces numeric strings to
    // numbers, which corrupts thread ids above 2^53.
    const threadId = $button.attr("data-thread-id");
    const isBlockAction = $button.hasClass("thread-action-block");

    if (threadId) {
      window.delete_thread(this, threadId, isBlockAction ? 1 : 0);
    }

    return false;
  });

  // Floating tooltip for thread action buttons — escapes the row's overflow:hidden clipping
  let $threadTooltip = null;
  const getThreadTooltip = () => {
    if (!$threadTooltip) {
      $threadTooltip = $('<span class="px-2 py-1 text-xs text-white bg-gray-900/80 rounded-lg whitespace-nowrap pointer-events-none transition-opacity" style="position:fixed;top:0;left:0;opacity:0;z-index:100;"></span>')
        .appendTo(document.body);
    }
    return $threadTooltip;
  };
  const showThreadTooltip = (btn) => {
    const text = btn.getAttribute('data-tooltip');
    if (!text) return;
    const $tip = getThreadTooltip().text(text);
    const tipEl = $tip[0];
    // Force measurement with visible layout (opacity 0 still measures)
    const btnRect = btn.getBoundingClientRect();
    const tipRect = tipEl.getBoundingClientRect();
    const left = btnRect.left + (btnRect.width / 2) - (tipRect.width / 2);
    const top = btnRect.top - tipRect.height - 6;
    tipEl.style.left = Math.max(4, left) + 'px';
    tipEl.style.top = top + 'px';
    tipEl.style.opacity = '1';
  };
  const hideThreadTooltip = () => {
    if ($threadTooltip) $threadTooltip[0].style.opacity = '0';
  };
  // The attribute alone, not a class beside it: the Requests rows carry the
  // same icon-only controls under their own class.
  $(document).on('mouseover focusin', '[data-tooltip]', function() {
    showThreadTooltip(this);
  });
  $(document).on('mouseout focusout', '[data-tooltip]', function() {
    hideThreadTooltip();
  });

  // The swipe gesture itself lives in the shared swipe-actions module. This maps
  // its action event onto the PM block/delete flow. Message request rows carry
  // report/delete cells instead and are handled by pm/requests.js.
  $(document).on('swipe-action', '.pm_thread', function(e) {
    const detail = e.originalEvent && e.originalEvent.detail;
    if (!detail || !detail.id) return;

    window.delete_thread(detail.button, detail.id, detail.action === 'block' ? 1 : 0);
  });

  // Initialize draggable separator
  window.initDraggableSeparator();
  window.initPmLayoutGeometryObserver();

  // Call resize on initial load and after a short delay to ensure all elements are properly loaded
  window.threads_main_resize();
  window.applyPlaceholderPreference();
  setTimeout(function() {
    window.threads_main_resize();
    window.applyPlaceholderPreference();
  }, 200);

  // Add a debounced resize handler to maintain correct sizing
  $(window).on('resize', function() {
    clearTimeout(window.resizeTimer);
    window.resizeTimer = setTimeout(function() {
      if (keyboardViewport) keyboardViewport.syncLayout(isTextEntry(document.activeElement));
      window.threads_main_resize();
      window.applyPlaceholderPreference();
    }, 250);
  });

  // Initialize photo upload and message sender modules
  // These are now loaded from separate files for better organization
});
